<template>
  <div class="theme-line-chart">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'ThemeLineChart',
  
  props: {
    categories: {
      type: Array,
      required: true
    },
    datasets: {
      type: Array,
      required: true
    },
    yAxisLabel: {
      type: String,
      default: ''
    },
    formatValue: {
      type: Function,
      default: (value) => value.toLocaleString()
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null,
      resizeObserver: null,
      chart: null
    };
  },

  mounted() {
    this.createChart();
    
    // Watch for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.isDarkMode = document.documentElement.classList.contains('dark');
      if (this.chart) {
        this.updateChart();
      }
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Watch for container resize
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
      
      if (this.$el) {
        this.resizeObserver.observe(this.$el);
      }
    }
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.destroy();
    }
  },

  watch: {
    categories() {
      this.updateChart();
    },
    datasets: {
      handler() {
        this.updateChart();
      },
      deep: true
    }
  },

  computed: {
    chartData() {
      if (!this.datasets || !Array.isArray(this.datasets)) {
        return {
          labels: [],
          datasets: []
        };
      }

      return {
        labels: this.categories,
        datasets: this.datasets.map((dataset, index) => ({
          label: dataset.label,
          data: dataset.data.map(v => parseFloat(v)),
          borderColor: this.getColor(index),
          backgroundColor: this.getColor(index, 0.1),
          borderWidth: 2.5,
          tension: 0.3,
          fill: false,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: this.getColor(index),
          pointBorderColor: this.isDarkMode ? '#0f172a' : '#ffffff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 3
        }))
      };
    },

    chartOptions() {
      const textColor = this.isDarkMode ? '#94a3b8' : '#64748b';
      const gridColor = this.isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.15)';
      const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
      const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            position: 'bottom',
            align: 'center',
            labels: {
              color: textColor,
              padding: 20,
              usePointStyle: true,
              pointStyle: 'circle',
              font: {
                size: 13,
                family: 'Inter, system-ui, sans-serif'
              },
              boxWidth: 8,
              boxHeight: 8
            }
          },
          tooltip: {
            backgroundColor: tooltipBg,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${this.formatValue(value)}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              color: textColor,
              font: {
                size: 12,
                family: 'Inter, system-ui, sans-serif'
              },
              padding: 8
            }
          },
          y: {
            grid: {
              color: gridColor,
              drawBorder: false,
              lineWidth: 1
            },
            ticks: {
              color: textColor,
              font: {
                size: 12,
                family: 'Inter, system-ui, sans-serif'
              },
              padding: 8,
              callback: (value) => {
                // Format as $X.XM
                if (value >= 1000000) {
                  return '$' + (value / 1000000).toFixed(1) + 'M';
                } else if (value >= 1000) {
                  return '$' + (value / 1000).toFixed(0) + 'K';
                }
                return '$' + value.toFixed(0);
              }
            },
            title: {
              display: !!this.yAxisLabel,
              text: this.yAxisLabel,
              color: textColor
            }
          }
        }
      };
    }
  },

  methods: {
    createChart() {
      if (!this.$refs.chartCanvas) {
        return;
      }

      const ctx = this.$refs.chartCanvas.getContext('2d');
      
      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new ChartJS(ctx, {
        type: 'line',
        data: this.chartData,
        options: this.chartOptions
      });
    },

    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }

      this.chart.data = this.chartData;
      this.chart.options = this.chartOptions;
      this.chart.update();
    },

    getColor(index, alpha = 1) {
      const colors = [
        '#3b82f6', // blue - for Total
        '#10b981', // teal/green - for Remaining
        '#f59e0b', // amber
        '#ef4444', // red
        '#8b5cf6', // purple
        '#ec4899'  // pink
      ];
      
      const color = colors[index % colors.length];
      
      if (alpha < 1) {
        // Convert hex to rgba
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
      
      return color;
    }
  }
};
</script>
